import {useLazyQuery, useQuery} from "@apollo/client";
import classNames from "classnames";
import {useEffect, useMemo, useState} from "react";
import {useFormContext} from "react-hook-form";
import {toast} from "react-hot-toast";
import {FiArrowLeft, FiExternalLink, FiSearch} from "react-icons/fi";
import {useDebounce} from "use-debounce";

import Button from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {FormLabel} from "@/components/form/FormLabel";
import {Icon} from "@/components/icon/Icon.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {UPTIME_FEEDBACK_URL} from "@/lib/constants/links.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {getFormError} from "@/lib/form/form.utils.ts";
import {
  CloudStatusGroupList,
  CloudStatusList,
  GQL_THIRD_PARTY_COMPONENTS,
  GQL_THIRD_PARTY_GROUPS,
  ThirdPartyService,
} from "@/lib/queries/checks.ts";
import {ThirdPartyServiceComponent} from "@/lib/types/checks.ts";
import {ThirdPartyComponent} from "@/pages/checks/components/form/fields/components/ThirdPartyComponent.tsx";
import {KEY_TAB} from "@/wcag.ts";

interface Props {
  onChange: (thirdPartyServiceComponent: ThirdPartyServiceComponent | null) => void;
}

export const ThirdPartySelectField = ({onChange}: Props) => {
  const limit = 20;
  const offset = 0;
  const [selectedGroup, setGroup] = useState("");
  const [search, setSearch] = useState("");
  const [searchDebounced] = useDebounce(search, 300);
  const [searchDisplay, setSearchDisplay] = useState<ThirdPartyServiceComponent[]>([]);
  const [lastSearch, setLastSearch] = useState(searchDebounced);
  const [lastSelectedGroup, setLastSelectedGroup] = useState(selectedGroup);

  const [getComponents, {data: componentData, loading: componentLoading, previousData: componentPreviousData}] =
    useLazyQuery<CloudStatusList>(GQL_THIRD_PARTY_COMPONENTS);
  const {data, loading, previousData} = useQuery<CloudStatusGroupList>(GQL_THIRD_PARTY_GROUPS, {
    variables: {
      limit: limit,
      offset: offset,
    },
  });

  const {
    watch,
    formState: {errors},
  } = useFormContext();
  const serviceNameError = getFormError("service_name", errors);
  const checkId = watch(ServiceField.ID);

  const currentData = loading ? previousData : data;
  const currentComponentsData = componentLoading ? componentPreviousData : componentData;
  const thirdPartyServices = useMemo(() => currentData?.cloudStatusGroups.items || [], [currentData]);
  const thirdPartyServiceComponents = useMemo(
    () => currentComponentsData?.cloudStatusMetas.items || [],
    [currentComponentsData]
  );

  useEffect(() => {
    if (serviceNameError) {
      toast.error(serviceNameError.toString());
    }
  }, [serviceNameError]);

  useEffect(() => {
    setSearchDisplay(thirdPartyServiceComponents);
    setLastSearch(searchDebounced);
    setLastSelectedGroup(selectedGroup);
    // eslint-disable-next-line
  }, [thirdPartyServiceComponents]);

  useEffect(() => {
    clearFieldError();
    getComponents({
      variables: {
        limit: limit,
        search: searchDebounced,
        group: selectedGroup,
      },
    });
    // eslint-disable-next-line
  }, [selectedGroup, searchDebounced, getComponents]);

  const goDeeper = (target: ThirdPartyService) => {
    setGroup(target.group);
  };

  const goBack = () => {
    setGroup("");
    setSearch("");
  };

  const clearFieldError = () => {
    if (errors.service_name) {
      errors.service_name.message = "";
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <div id="third-party-selector">
      <div className="d-flex gap-3 flex-md-row flex-column align-items-md-end mb-4">
        <div className="flex-grow-1">
          <FormLabel forId="service_name" required={true} className="h5">
            Third Party Services
          </FormLabel>
          <SearchInput
            id="service_name"
            value={search}
            onChange={setSearch}
            placeholder={"Search"}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="flex-shrink-0">
          <Button variant="secondary" onClick={() => window.open(UPTIME_FEEDBACK_URL, "_blank")}>
            Request a New Service <Icon icon={FiExternalLink} />
          </Button>
        </div>
      </div>

      {thirdPartyServiceComponents.length > 0 && (searchDebounced || selectedGroup) && (
        <Button variant="secondary" onClick={goBack} className="mb-2">
          <Icon icon={FiArrowLeft} />
          Back
        </Button>
      )}

      <div
        className={classNames("cloud-status-selector-area", {
          short: thirdPartyServiceComponents.length > 0 && (searchDebounced || selectedGroup),
        })}
      >
        {!selectedGroup && !searchDebounced && (
          <div className="row row-cols-1 row-cols-sm-2 g-3 pe-1">
            {thirdPartyServices.map((third_party: ThirdPartyService) => {
              return (
                <div className="col" key={"third-party-" + third_party.group}>
                  <Button
                    onClick={() => goDeeper(third_party)}
                    onKeyDown={(e) => {
                      if (e.key !== KEY_TAB) {
                        goDeeper(third_party);
                      }
                    }}
                    variant="cloudStatusS"
                  >
                    <CDNImage
                      className="me-3 cloud-status-icon"
                      alt={`${third_party.group} logo`}
                      src={third_party.logo_src}
                    />
                    <div className="pulseTextBodyBold">{third_party.group}</div>
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {selectedGroup && selectedGroup !== lastSelectedGroup && <LoadingContainer className="pb-5" />}

        {searchDisplay.length > 0 && selectedGroup && selectedGroup === lastSelectedGroup && !searchDebounced && (
          <div className="row row-cols-1 g-3 pe-1">
            {searchDisplay.map((thirdPartyServiceComponent) => {
              return (
                <div className="col" key={"components-" + thirdPartyServiceComponent.id}>
                  <ThirdPartyComponent
                    {...thirdPartyServiceComponent}
                    onClick={() => {
                      onChange(thirdPartyServiceComponent);
                    }}
                    disabled={Boolean(checkId)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {searchDebounced && (
          <>
            {searchDebounced !== lastSearch && <LoadingContainer className="pb-5" />}
            {searchDisplay.length > 0 && searchDebounced === lastSearch && (
              <div className="row row-cols-1 g-3 pe-1">
                {searchDisplay.map((thirdPartyServiceComponent) => (
                  <div className="col" key={"components-" + thirdPartyServiceComponent.id}>
                    <ThirdPartyComponent
                      {...thirdPartyServiceComponent}
                      onClick={() => {
                        onChange(thirdPartyServiceComponent);
                      }}
                      disabled={Boolean(checkId)}
                    />
                  </div>
                ))}
              </div>
            )}
            {searchDisplay.length == 0 && (
              <Card variant="white" className="d-flex flex-column align-items-center justify-content-center py-5">
                <Icon icon={FiSearch} className="pulse-color-gray-500 mb-4" removeDivDimensions />
                <div className="pulseTextH3Bold mb-2">No Service found.</div>
                <Button size="fitContent" variant="ghost" onClick={() => window.open(UPTIME_FEEDBACK_URL, "_blank")}>
                  Request a New Service <Icon icon={FiExternalLink} />
                </Button>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};
